import React, { useEffect, useRef, useState } from 'react';
import { Html5Qrcode } from 'html5-qrcode';
import api from '../api';
import TimeLibrary from '../libraries/Time';

const READER_ID = 'parking-scan-reader';

/**
 * Standalone staff scanner (no login) for BernExpo QR parking tickets. Opened
 * via a link with a shared secret: /parking-scan?key=<secret>. Scans the raw
 * BernExpo code and marks the ticket used via the API.
 * @component
 */
export default () => {
  const key = new URLSearchParams(window.location.search).get('key') || '';
  const scannerRef = useRef(null);
  const busyRef = useRef(false);
  const [result, setResult] = useState(null);

  const stop = async () => {
    const s = scannerRef.current;
    if (s && s.isScanning) {
      try {
        await s.stop();
      } catch (e) {
        /* ignore */
      }
    }
  };

  const onScan = async (code) => {
    if (busyRef.current) return;
    busyRef.current = true;
    await stop();
    try {
      const res = await api.parking.scan({ code, key });
      setResult(res.data || { status: 'invalid' });
    } catch (e) {
      setResult({ status: e && e.status === 401 ? 'unauthorized' : 'invalid' });
    }
  };

  const start = async () => {
    setResult(null);
    busyRef.current = false;
    try {
      if (!scannerRef.current) scannerRef.current = new Html5Qrcode(READER_ID);
      await scannerRef.current.start(
        { facingMode: 'environment' },
        { fps: 10, qrbox: 250 },
        onScan,
        () => {},
      );
    } catch (e) {
      setResult({ status: 'camera_error' });
    }
  };

  useEffect(() => {
    if (key) start();
    return () => {
      stop();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const palette = {
    success: { bg: '#1b7a35', label: 'Erfolgreich gescannt' },
    already_used: { bg: '#c47d00', label: 'Bereits gescannt' },
    invalid: { bg: '#b3261e', label: 'Ungültiges Ticket' },
    unauthorized: { bg: '#b3261e', label: 'Scan-Link ungültig – Schlüssel prüfen' },
    not_valid_now: { bg: '#b3261e', label: 'Nicht gültig – gilt am Spieltag 16:00 bis 04:00' },
    camera_error: { bg: '#b3261e', label: 'Kamera nicht verfügbar' },
  };

  const page = {
    minHeight: '100vh',
    background: '#0e0e12',
    color: '#fff',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '24px 16px',
    fontFamily: 'Industry-Book, sans-serif',
  };

  if (!key) {
    return (
      <div style={page}>
        <h2>SCB Parking – Scanner</h2>
        <p style={{ marginTop: 40 }}>Kein Zugang. Der Link ist ungültig.</p>
      </div>
    );
  }

  const info = result && palette[result.status];

  return (
    <div style={page}>
      <h2 style={{ marginBottom: 16 }}>SCB Parking – Scanner</h2>

      {/* Reader stays mounted; html5-qrcode fills it while scanning. */}
      <div
        id={READER_ID}
        style={{ width: '100%', maxWidth: 360, display: result ? 'none' : 'block' }}
      />

      {!result && (
        <p style={{ opacity: 0.7, marginTop: 16 }}>QR-Code vor die Kamera halten.</p>
      )}

      {result && (
        <div style={{ width: '100%', maxWidth: 360, textAlign: 'center' }}>
          <div
            style={{
              background: (info && info.bg) || '#b3261e',
              borderRadius: 10,
              padding: '22px 16px',
              fontSize: 20,
              fontWeight: 'bold',
            }}
          >
            {(info && info.label) || 'Ungültiges Ticket'}
          </div>

          {(result.status === 'success' || result.status === 'already_used' || result.status === 'not_valid_now') && (
            <div style={{ marginTop: 14, lineHeight: 1.7 }}>
              {result.field && <div>{result.field}</div>}
              {result.opponent && <div>SC Bern – {result.opponent}</div>}
              {result.game_date && <div>{TimeLibrary.date(result.game_date)}</div>}
            </div>
          )}

          <button
            onClick={start}
            style={{
              marginTop: 24,
              background: '#e73c37',
              color: '#fff',
              border: 'none',
              borderRadius: 8,
              padding: '14px 26px',
              fontSize: 16,
              fontWeight: 'bold',
              cursor: 'pointer',
            }}
          >
            Nächstes Ticket scannen
          </button>
        </div>
      )}
    </div>
  );
};
